import type { AgentDto } from "@agent/data/dto/agent.dto";
import { AgentDtoSchema } from "@agent/data/dto/agent.dto";
import { PathogenTypeEnumMapper } from "@agent/data/mappers/pathogen-type-enum.mapper";
import { Agent } from "@agent/domain/models/agent.model";
import { CropTypeMapper } from "@campaign/data/mappers/crop-type.mapper";
import type { CropType } from "@campaign/domain/models/crop-type.model";
import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class AgentMapper {
    constructor(
        @inject(PathogenTypeEnumMapper)
        private readonly pathogenTypeEnumMapper: PathogenTypeEnumMapper,
        @inject(CropTypeMapper)
        private readonly cropTypeMapper: CropTypeMapper,
    ) {}

    map(agentDto: AgentDto): Result<Agent, ModelError<typeof Agent>> {
        const agentDtoParsed = AgentDtoSchema.safeParse(agentDto);

        if (!agentDtoParsed.success) {
            console.error(agentDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const pathogenType = this.pathogenTypeEnumMapper.map(
            agentDtoParsed.data.pathogen_type,
        );

        const cropTypes = agentDtoParsed.data.crop_types
            ? (this.cropTypeMapper.mapList(agentDtoParsed.data.crop_types)
                  .cropTypes as CropType[])
            : undefined;

        return Agent.createWithRecovery({
            id: agentDtoParsed.data.id,
            name: agentDtoParsed.data.name,
            code: agentDtoParsed.data.code,
            description: agentDtoParsed.data.description,
            pathogenType,
            cropTypes,
        });
    }

    mapList(agentsDto: AgentDto[]): Agent[] {
        return agentsDto.mapNotNull((agentDto) =>
            this.map(agentDto).match(
                (agent) => agent,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping agents:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );
    }
}
